import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Bean, AlertTriangle, ShieldCheck, PackagePlus, Minus, Plus, Trash2, TrendingDown, Mail, Copy, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CAFE } from "@/constants/testIds";
import { toast } from "sonner";

const STATUS_STYLES = {
  critical: {
    ring: "border-destructive/40 bg-destructive/5",
    chip: "bg-destructive text-destructive-foreground",
    label: "REORDER NOW",
    Icon: AlertTriangle,
  },
  watch: {
    ring: "border-chart-3/40 bg-chart-3/5",
    chip: "bg-chart-3/20 text-chart-3",
    label: "WATCH",
    Icon: TrendingDown,
  },
  healthy: {
    ring: "border-accent/30 bg-accent/5",
    chip: "bg-accent/20 text-accent",
    label: "HEALTHY",
    Icon: ShieldCheck,
  },
  unknown: {
    ring: "border-border bg-secondary/50",
    chip: "bg-muted text-muted-foreground",
    label: "NO DATA",
    Icon: Bean,
  },
};

export default function CoffeeInventory() {
  const [items, setItems] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({ name: "", origin: "", roast_level: "medium", unit_type: "kg", on_hand: "", supplier: "", supplier_email: "" });
  const [consumeFor, setConsumeFor] = useState(null); // {id, name, action}
  const [poFor, setPoFor] = useState(null); // {id, name}
  const [po, setPo] = useState(null); // {subject, body, recommended_qty, ...}
  const [poLoading, setPoLoading] = useState(false);

  const load = async () => setItems((await api.get("/cafe/coffee-inventory")).data);
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/cafe/coffee-inventory", { ...form, on_hand: Number(form.on_hand), supplier_email: form.supplier_email || null });
      toast.success(`${form.name} added.`);
      setOpenNew(false);
      setForm({ name: "", origin: "", roast_level: "medium", unit_type: "kg", on_hand: "", supplier: "", supplier_email: "" });
      await load();
    } catch { toast.error("Could not save item."); }
  };

  const doQty = async (qty) => {
    if (!consumeFor) return;
    const { id, action, name } = consumeFor;
    // Look up the SKU up-front so we still have its supplier email if the
    // API response comes back before we need it.
    const sku = items.find((i) => i.id === id);
    try {
      const { data } = await api.post(
        `/cafe/coffee-inventory/${id}/${action}`,
        { quantity: qty, source: "manual" },
      );
      const unit = data?.unit_type || sku?.unit_type || "kg";
      toast.success(`${name} · ${action === "restock" ? "+" : "-"}${qty} ${unit}`);
      setConsumeFor(null);
      await load();

      // On restock, if the SKU has a supplier email, hand the owner a
      // pre-filled email so they can notify the roaster of the received
      // delivery (or reorder request) with a single Send.
      if (action === "restock" && sku?.supplier_email) {
        const businessName = sku?.tenant_name || "our café";
        const subject = `Restock notice — ${qty} ${unit} of ${name}`;
        const body =
          `Hi ${sku.supplier || "team"},\n\n` +
          `Kairosa is confirming a restock of ${qty} ${unit} of ${name}` +
          `${sku.origin ? ` (${sku.origin})` : ""}` +
          `${sku.roast_level ? `, ${sku.roast_level} roast` : ""}.\n\n` +
          `Current on-hand after restock: ${data?.on_hand ?? "—"} ${unit}.\n` +
          `Please reply if there is anything we need to reconcile.\n\n` +
          `Thanks,\n${businessName}`;
        const url = `mailto:${encodeURIComponent(sku.supplier_email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        // Use assign so the user's default mail app opens without leaving Kairosa.
        window.location.href = url;
        toast.info(`Opening your email to ${sku.supplier_email}…`);
      } else if (action === "restock" && !sku?.supplier_email) {
        toast.info("Add a supplier email on this SKU to auto-notify next time.");
      }
    } catch {
      toast.error("Update failed.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this coffee SKU and its history?")) return;
    await api.delete(`/cafe/coffee-inventory/${id}`);
    await load();
  };

  const openDraftPO = async (item) => {
    setPoFor({ id: item.id, name: item.name });
    setPo(null);
    setPoLoading(true);
    try {
      const { data } = await api.post(`/cafe/coffee-inventory/${item.id}/draft-po`);
      setPo(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not draft PO email.";
      toast.error(msg);
      setPoFor(null);
    } finally {
      setPoLoading(false);
    }
  };

  const copyEmail = async () => {
    if (!po) return;
    const text = `Subject: ${po.subject}\n\n${po.body}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Email copied to clipboard.");
    } catch {
      toast.error("Copy failed — select the text manually.");
    }
  };

  const alerts = items.filter((i) => i.reorder.status === "critical");
  const watches = items.filter((i) => i.reorder.status === "watch");

  return (
    <div className="px-4 md:px-8 py-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">Café</div>
          <h1 className="font-display text-4xl">Coffee inventory</h1>
          <p className="text-muted-foreground text-sm mt-1 max-w-xl">
            Kairosa watches your consumption pattern and flags reorder when a SKU has fewer than{" "}
            <b>10 days</b> of stock at current pace.
          </p>
        </div>
        <Button data-testid={CAFE.invNew} onClick={() => setOpenNew(true)} className="rounded-full gap-2">
          <PackagePlus className="h-4 w-4" /> New coffee SKU
        </Button>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Total SKUs</div>
          <div className="font-display text-4xl mt-2">{items.length}</div>
        </div>
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] text-destructive">Reorder now</div>
          <div className="font-display text-4xl mt-2">{alerts.length}</div>
        </div>
        <div className="rounded-xl border border-chart-3/30 bg-chart-3/5 p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] text-chart-3">Watch list</div>
          <div className="font-display text-4xl mt-2">{watches.length}</div>
        </div>
      </div>

      {/* Item cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((it) => {
          const style = STATUS_STYLES[it.reorder.status] || STATUS_STYLES.unknown;
          const S = style.Icon;
          const pct = Math.min(100, Math.max(4, (it.reorder.days_remaining || 0) / 30 * 100));
          const barColor =
            it.reorder.status === "critical" ? "bg-destructive"
            : it.reorder.status === "watch" ? "bg-chart-3"
            : it.reorder.status === "healthy" ? "bg-accent"
            : "bg-muted-foreground";

          return (
            <div
              key={it.id}
              data-testid={CAFE.invItem(it.id)}
              className={"relative rounded-xl border p-5 " + style.ring}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="min-w-0">
                  <div className="font-display text-xl leading-tight truncate">{it.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {it.origin || "—"} · {it.roast_level || "—"}
                  </div>
                </div>
                <span
                  data-testid={CAFE.invStatus(it.id)}
                  className={"inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] rounded-full px-2 py-0.5 " + style.chip}
                >
                  <S className="h-3 w-3" />
                  {style.label}
                </span>
              </div>

              <div className="flex items-baseline gap-2">
                <div className="font-display text-4xl">{it.on_hand}</div>
                <div className="text-xs text-muted-foreground">{it.unit_type} on hand</div>
              </div>

              <div className="mt-3 text-xs text-muted-foreground">{it.reorder.message}</div>

              {/* Bar: days remaining vs 30 */}
              <div className="mt-3">
                <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                  <div
                    className={"h-full " + barColor + " transition-all"}
                    style={{ width: pct + "%" }}
                  />
                </div>
                <div className="flex justify-between text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-1">
                  <span>{it.reorder.days_remaining ?? "—"} days</span>
                  <span>avg {it.analytics.daily_avg} {it.unit_type}/day</span>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  data-testid={CAFE.invConsume(it.id)}
                  onClick={() => setConsumeFor({ id: it.id, name: it.name, action: "consume" })}
                  size="sm"
                  variant="outline"
                  className="rounded-full flex-1 gap-1"
                >
                  <Minus className="h-3.5 w-3.5" /> Log usage
                </Button>
                <Button
                  data-testid={CAFE.invRestock(it.id)}
                  onClick={() => setConsumeFor({ id: it.id, name: it.name, action: "restock" })}
                  size="sm"
                  className="rounded-full flex-1 gap-1"
                >
                  <Plus className="h-3.5 w-3.5" /> Restock
                </Button>
                <button onClick={() => remove(it.id)} className="text-muted-foreground hover:text-destructive p-2">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>

              {(it.reorder.status === "critical" || it.reorder.status === "watch") && (
                <Button
                  data-testid={`coffee-inv-draft-po-${it.id}`}
                  onClick={() => openDraftPO(it)}
                  size="sm"
                  variant="ghost"
                  className="mt-2 w-full rounded-full gap-1.5 text-primary hover:text-primary hover:bg-primary/10"
                >
                  <Sparkles className="h-3.5 w-3.5" /> Draft reorder email to supplier
                </Button>
              )}
            </div>
          );
        })}
        {items.length === 0 && (
          <div className="col-span-full text-sm text-muted-foreground text-center py-12">
            No coffee SKUs yet. Add your first bean to enable reorder alerts.
          </div>
        )}
      </div>

      {/* New SKU dialog */}
      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-2xl">New coffee SKU</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <div><Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1.5" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Origin</Label>
                <Input value={form.origin} onChange={(e) => setForm({ ...form, origin: e.target.value })} className="mt-1.5" placeholder="Ethiopia" />
              </div>
              <div><Label>Roast</Label>
                <Select value={form.roast_level} onValueChange={(v) => setForm({ ...form, roast_level: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="light">Light</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="dark">Dark</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>On hand</Label>
                <Input type="number" step="0.01" value={form.on_hand} onChange={(e) => setForm({ ...form, on_hand: e.target.value })} required className="mt-1.5" />
              </div>
              <div><Label>Unit</Label>
                <Select value={form.unit_type} onValueChange={(v) => setForm({ ...form, unit_type: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kg">kg</SelectItem>
                    <SelectItem value="lbs">lbs</SelectItem>
                    <SelectItem value="units">units</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Supplier (optional)</Label>
                <Input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} className="mt-1.5" placeholder="Onyx Roasters" />
              </div>
              <div><Label>Supplier email</Label>
                <Input
                  data-testid="coffee-inv-supplier-email"
                  type="email"
                  value={form.supplier_email}
                  onChange={(e) => setForm({ ...form, supplier_email: e.target.value })}
                  className="mt-1.5"
                  placeholder="sales@…"
                />
              </div>
            </div>
            <DialogFooter className="pt-2">
              <Button type="submit" className="rounded-full">Add SKU</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Consume / Restock dialog */}
      <Dialog open={!!consumeFor} onOpenChange={(o) => !o && setConsumeFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {consumeFor?.action === "restock" ? "Restock" : "Log usage"} · {consumeFor?.name}
            </DialogTitle>
            <DialogDescription>
              {consumeFor?.action === "restock"
                ? "Add stock from a supplier delivery. Restock events don't count as usage."
                : "Deduct from on-hand stock. This event feeds into your 14-day daily average."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-4 gap-2">
            {[0.1, 0.25, 0.5, 1, 2, 5, 10, 25].map((q) => (
              <Button
                key={q}
                onClick={() => doQty(q)}
                variant="outline"
                className="rounded-full"
              >
                {q} kg
              </Button>
            ))}
          </div>
          <div className="mt-3 text-xs text-muted-foreground">
            Or enter a custom amount:
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const v = Number(e.target.elements.q.value);
              if (v > 0) doQty(v);
            }}
            className="flex gap-2 mt-1"
          >
            <Input name="q" type="number" step="0.01" placeholder="e.g. 3.5" required />
            <Button type="submit" className="rounded-full">Apply</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* AI-drafted PO email dialog */}
      <Dialog
        open={!!poFor}
        onOpenChange={(o) => { if (!o) { setPoFor(null); setPo(null); } }}
      >
        <DialogContent data-testid="coffee-inv-po-dialog" className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Mail className="h-5 w-5" /> Reorder email · {poFor?.name}
            </DialogTitle>
            <DialogDescription>
              Kairosa AI drafted this using your 14-day consumption pattern. Review, tweak, then copy to your email client.
            </DialogDescription>
          </DialogHeader>

          {poLoading && (
            <div data-testid="coffee-inv-po-loading" className="py-10 text-center text-sm text-muted-foreground">
              <Sparkles className="h-5 w-5 mx-auto mb-2 animate-pulse" />
              Drafting with Claude Sonnet 4.5…
            </div>
          )}

          {po && !poLoading && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="rounded-md border border-border bg-secondary/40 p-2">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Recommend</div>
                  <div className="font-display text-lg" data-testid="coffee-inv-po-qty">
                    {po.recommended_qty} {po.unit}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-secondary/40 p-2">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">On hand</div>
                  <div className="font-display text-lg">{po.on_hand} {po.unit}</div>
                </div>
                <div className="rounded-md border border-border bg-secondary/40 p-2">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Days left</div>
                  <div className="font-display text-lg">{po.days_remaining ?? "—"}</div>
                </div>
              </div>

              <div>
                <Label>Subject</Label>
                <Input
                  data-testid="coffee-inv-po-subject"
                  value={po.subject}
                  onChange={(e) => setPo({ ...po, subject: e.target.value })}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Body</Label>
                <Textarea
                  data-testid="coffee-inv-po-body"
                  value={po.body}
                  onChange={(e) => setPo({ ...po, body: e.target.value })}
                  rows={10}
                  className="mt-1.5 font-mono text-xs"
                />
              </div>

              {(po.supplier || po.supplier_email) && (
                <div className="text-xs text-muted-foreground">
                  Send to: <b className="text-foreground">{po.supplier_email || po.supplier}</b>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="pt-2 gap-2">
            {po?.supplier_email && (
              <a
                href={`mailto:${po.supplier_email}?subject=${encodeURIComponent(po.subject)}&body=${encodeURIComponent(po.body)}`}
                data-testid="coffee-inv-po-mailto"
              >
                <Button variant="outline" className="rounded-full gap-1.5">
                  <Mail className="h-4 w-4" /> Open in email
                </Button>
              </a>
            )}
            <Button
              data-testid="coffee-inv-po-copy"
              onClick={copyEmail}
              disabled={!po}
              className="rounded-full gap-1.5"
            >
              <Copy className="h-4 w-4" /> Copy email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
